import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { albumQueries, useAlbum } from '@/lib/services/albums/albums.query'
import { idSchema, isSafeRelativePath } from '@/lib/utils'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { z } from 'zod/v4'

import AlbumEditor from '@/components/gallery/AlbumEditor'

const paramsSchema = z.object({
  albumId: idSchema('album'),
})

const sanitizeReturnTo = (value) => {
  if (typeof value !== 'string') {
    return undefined
  }

  const trimmedValue = value.trim()
  return isSafeRelativePath(trimmedValue, 'https://iris.local') ? trimmedValue : undefined
}

const searchSchema = z.object({
  returnTo: z.string().optional().transform(sanitizeReturnTo),
})

export const Route = createFileRoute('/_authenticated/$orgId/gallery/album/$albumId')({
  component: RouteComponent,
  head: adminRouteTitleHead,
  params: { parse: (params) => paramsSchema.parse(params) },
  validateSearch: (search) => searchSchema.parse(search),
  loader: async ({ context, params }) => {
    if (!context.ability?.can('list', 'albums')) throw notFound()

    if (+params.orgId === +import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID) {
      return { isReadOnly: true }
    }

    const album = await context.queryClient.ensureQueryData(
      albumQueries.show({
        organizationId: params.orgId,
        albumId: params.albumId,
      })
    )
    return { album, isReadOnly: !context.ability.can('edit', 'albums') }
  },
})

function RouteComponent() {
  return (
    <IfGlobalOrg
      show={false}
      fallback={
        <OrganizationContextNotice className="mb-4">
          Gallery is organization-specific.
        </OrganizationContextNotice>
      }
    >
      <AlbumContent />
    </IfGlobalOrg>
  )
}

function AlbumContent() {
  const { albumId } = Route.useParams()
  const { returnTo } = Route.useSearch()
  const { data: album } = useAlbum(albumId)
  return <AlbumEditor album={album} returnTo={returnTo} />
}
